'use client';
/**
 * ⑭ R14a — 탐색 v2 의 «보강» 섬. 아무것도 그리지 않는다(null) — 서버 HTML 불변.
 *
 *   ① 토스 미니앱 모드: <html class="toss-mode"> (옛 Navigation 과 같은 판정 · CSS 가 마스트·탭바를 숨긴다)
 *   ② 글자 크기 복원(localStorage) + 프로필 선호 동기화 — 옛 Navigation 에서 그대로 옮김
 *   ③ '/' 단축키 → /search (옛 Navigation 과 같은 동작)
 *   ④ 계측 위임 리스너 — [data-nav-ev] 클릭 → nav_click · tab_click · more_click
 *   ⑤ 드롭다운 보강 — 바깥 클릭·Esc 로 닫기, 하나 열면 나머지 닫기, 경로가 바뀌면 닫고 포커스 해제
 *      (CSS 만으로도 열리고 닫힌다. 이 섬이 없거나 늦어도 링크는 전부 동작한다.)
 */
import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/components/AuthProvider';
import { isTossMode } from '@/lib/toss-mode';
import { applyFontClass, isFontSize, readSavedFont, FONT_KEY } from '@/lib/nav/font-size';
import { handleNavClickTarget } from '@/lib/nav/nav-track';
import { trackAdvertiseClick } from '@/components/ads/acq/track';
import { MORE_AD_FROM } from '@/lib/nav/nav-config';

const OPEN_SEL = '.kd-mast details[open], .kd-user details[open]';

function closeAll(except?: Element | null) {
  document.querySelectorAll(OPEN_SEL).forEach((d) => {
    if (d !== except) d.removeAttribute('open');
  });
}

export default function NavV2Effects() {
  const pathname = usePathname();
  const router = useRouter();
  const { profile } = useAuth();

  // ① + ② 초기화(1회)
  useEffect(() => {
    if (isTossMode()) document.documentElement.classList.add('toss-mode');
    const saved = readSavedFont();
    if (saved) applyFontClass(saved);
  }, []);

  // ② 프로필 선호 동기화
  useEffect(() => {
    const pref = profile?.fontSizePref;
    if (!isFontSize(pref)) return;
    try {
      if (localStorage.getItem(FONT_KEY) !== pref) {
        localStorage.setItem(FONT_KEY, pref);
        applyFontClass(pref);
      }
    } catch { /* 저장소 막힘 */ }
  }, [profile?.fontSizePref]);

  // ③ '/' 단축키
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // 닫고 포커스를 뺀다(포커스가 남으면 :focus-visible 이 패널을 다시 연다).
        const act = document.activeElement as HTMLElement | null;
        closeAll();
        if (act && act.closest('.kd-mast .kd-dd, .kd-user')) act.blur();
        return;
      }
      const tag = (e.target as HTMLElement | null)?.tagName ?? '';
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) && !(e.target as HTMLElement | null)?.isContentEditable) {
        e.preventDefault();
        router.push('/search');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [router]);

  // ④ 계측 + ⑤ 바깥 클릭 닫기
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const t = e.target as Element | null;
      try {
        const hit = handleNavClickTarget(t);
        // more-tidy — 더보기 「광고」 묶음(/more · 헤더 더보기 패널) 클릭은 광고 유입 계측(advertise_click · from=more)에도 남긴다.
        if (hit?.ev === 'more_click' && hit.group === 'ad') trackAdvertiseClick(MORE_AD_FROM);
      } catch { /* 계측 실패는 이동을 막지 않는다 */ }
      // 마우스로 열린 토글을 다시 눌러 닫을 때 — 포커스가 남으면 :focus-within 이 패널을 붙잡는다 → 닫힌 뒤 포커스를 푼다.
      const sum = t?.closest?.('.kd-mast summary, .kd-user summary') as HTMLElement | null;
      if (sum && e.detail > 0 && (sum.parentElement as HTMLDetailsElement | null)?.open) setTimeout(() => sum.blur(), 0);
      const inside = t?.closest?.('details');
      closeAll(inside && inside.matches(OPEN_SEL.replace(/\[open\]/g, '')) ? inside : null);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // ⑤ 이동 뒤 닫기 — 클릭한 링크에 포커스가 남아 :focus-within 이 패널을 붙잡지 않게.
  useEffect(() => {
    closeAll();
    const act = document.activeElement as HTMLElement | null;
    if (act && act.closest('.kd-mast .kd-dd, .kd-user')) act.blur();
  }, [pathname]);

  return null;
}
